<template>
  <div class="car-change-input">
    <div class="form-group text-center">
      <template v-if="!changedVehicle || changedVehicle.uuid === originalCar.uuid">
        <img :src="carImage"/>
        <p>
          <label :for="options.name">{{ options.label }}</label>
          {{ originalCar.CarType.name }} (#{{ originalCar.convadisId }} - {{ originalCar.name }})
        </p>
        <button class="btn btn-primary" @click="openChangeVehicleModal()" type="button">Change Vehicle</button>
      </template>
    </div>
    <div class="form-group text-center">
      <template v-if="changedVehicle && changedVehicle.uuid !== originalCar.uuid">
        <p><strong>Vehicle Changed From</strong></p>
        <img height="50" :src="carImage"/>
        <p class="small">
          {{ originalCar.CarType.name }} (#{{ originalCar.convadisId }} - {{ originalCar.name }})
        </p>
        <span class="fa fa-2x fa-angle-double-down"></span><br/>
        <img :src="changedCarImage"/>
        <p>
          <label>New Vehicle</label>
          {{ changedVehicle.CarType.name }} (#{{ changedVehicle.convadisId }} - {{ changedVehicle.name }})
        </p>
        <button class="btn btn-default" @click="openChangeVehicleModal()" type="button">Change Vehicle</button>
      </template>
    </div>
    <modal title="Change Vehicle"
      @submit="changeVehicle()"
      @dismiss="closeChangeVehicleModal()"
      :is-open="isChangingVehicle"
      :action-button="{ label: 'Change Vehicle' }"
      :dismiss-button="{ label: 'Cancel Vehicle Change' }">
      <slot>
        <div class="form-group row">
          <h4 class="col-sm-12">Search for a specific vehicle:</h4>
        </div>
        <object-select-input :options="fields.carNumber" v-model="fields.carNumber.value" @input="search()"></object-select-input>
        <objects-select-input :options="fields.carModels" ref="carModels" v-model="fields.carModels.value" @input="search()"></objects-select-input>
        <objects-select-input :options="fields.carFeatures" ref="carFeatures" v-model="fields.carFeatures.value" @input="search()"></objects-select-input>
        <!-- checkbox input for unavailable cars -->
        <div class="text-right btn-margin">
          <strong>Refresh Cars</strong> &nbsp;
          <button class="btn btn-default" type="button" @click="search()">
            <span class="fa fa-sync"></span>
          </button>
        </div>
        <nav-tab :tab-count="2" @change="tabChanged($event)" ref="tabs">
          <!-- Car List -->
          <div title="List" slot="tab1" icon="fa-list">
            <div class="list-group-item text-center">
              <paging-input @change="updatePage($event)" :options="{
                totalResults: totalAvailableCars,
                current,
                limit: 6
              }">
              </paging-input>
              <template v-if="availableCars.length">
                <div class="row">
                  <template v-for="car in availableCars">
                    <car-tile :selected="selectedCar" :key="car.uuid"
                      @carChanged="vehicleUpdated($event)"
                      :current="originalCar.uuid"
                      :car="car">
                    </car-tile>
                  </template>
                </div>
              </template>
              <template v-else>
                <p class="alert alert-danger">No results.</p>
              </template>
              <paging-input @change="updatePage($event)" :options="{
                totalResults: totalAvailableCars,
                current,
                limit: 6
                }">
              </paging-input>
            </div>
          </div>
          <!-- Car Map -->
          <div title="Map" slot="tab2" icon="fa-map">
            <div class="list-group-item">
              <reservation-map ref="map"
                :cars="availableCars"
                @location="updateLocation($event)"
                @reserve="reserve($event)"
                :height="450">
              </reservation-map>
            </div>
          </div>
        </nav-tab>
      </slot>
    </modal>
  </div>
</template>

<script>
import Modal from '@/components/Modal'
import NavTab from '@/components/NavTab'
import ReservationMap from '@/components/ReservationMap'
import CarTile from '@/components/displays/CarTile'
import PagingInput from '@/components/inputs/PagingInput'
import ObjectSelectInput from '@/components/inputs/ObjectSelectInput'
import ObjectsSelectInput from '@/components/inputs/ObjectsSelectInput'
import Api from '@/vuex/api'
import {
  validationClass,
  validationIcon,
  validationMessage
} from '@/util/helpers'
import {
  OPEN_MODAL,
  CLOSE_MODAL,
  GET_FORM_VALUE,
  GET_FORM_FIELD,
  LOAD_CAR_FEATURES,
  LOAD_CAR_LIST,
  MODIFY_FORM_FIELD
} from '@/vuex/types'
import { mapGetters } from 'vuex'

export default {
  name: 'CarChangeInput',
  components: {
    CarTile,
    Modal,
    NavTab,
    ObjectSelectInput,
    ObjectsSelectInput,
    PagingInput,
    ReservationMap
  },
  props: {
    value: {
      type: String
    },
    options: {
      type: Object,
      default () {
        return {
          value: '',
          Car: {}
        }
      }
    }
  },
  computed: {
    validationClass,
    validationIcon,
    validationMessage,
    carImage () {
      return `https://techcar-dev.s3.amazonaws.com/${this.originalCar.CarType.AmazonImage.objectKey}`
    },
    changedCarImage () {
      return `https://techcar-dev.s3.amazonaws.com/${this.changedVehicle.CarType.AmazonImage.objectKey}`
    },
    ...mapGetters([
      GET_FORM_VALUE,
      GET_FORM_FIELD,
      'carFeatures',
      'carList'
    ])
  },
  data () {
    return {
      isChangingVehicle: false,
      isDirty: false,
      isValid: false,
      availableCars: [],
      totalAvailableCars: 0,
      current: 1,
      fields: {
        carNumber: {
          label: 'Car Number',
          name: 'convadisId',
          objects: [],
          ignoreSort: true,
          objectName: 'dropdownLabel',
          value: ''
        },
        carModels: {
          label: 'Car Models',
          name: 'CarTypeUuid',
          objects: [],
          objectName: 'dropdownLabel',
          labelName: 'className',
          value: []
        },
        carFeatures: {
          label: 'Car Features',
          name: 'CarFeatures',
          objectName: 'name',
          iconName: 'icon',
          objects: [],
          value: []
        }
      },
      selectedCar: '',
      originalCar: {
        uuid: '',
        CarType: {
          AmazonImage: {}
        }
      },
      changedVehicle: false
    }
  },
  async mounted () {
    this.originalCar = this.options.Car
    this.selectedCar = this.options.Car.uuid
    let AccountUuid = this[GET_FORM_VALUE]({
      permalink: 'editReservation',
      name: 'AccountUuid'
    })
    await this.$store.dispatch(LOAD_CAR_FEATURES, AccountUuid)
    await this.$store.dispatch(LOAD_CAR_LIST, AccountUuid)
    this.fields.carNumber.objects = this.carList
    this.fields.carFeatures.objects = this.carFeatures
    this.validate(this.value)
    this.isDirty = false
  },
  methods: {
    async openChangeVehicleModal () {
      this.current = 1
      this.isChangingVehicle = true
      this.$store.dispatch(OPEN_MODAL, {
        title: 'Change Vehicle',
        alreadyOpen: true
      })
      await this.search()
    },
    async search () {
      let { endDate, startDate } = this[GET_FORM_VALUE]({
        permalink: 'editReservation',
        name: 'dateRange'
      })
      let carModel = this.fields.carModels.value
      let carFeatures = this.fields.carFeatures.value
      let carNumber = this.fields.carNumber.value
      let AccountUuid = this[GET_FORM_VALUE]({
        permalink: 'editReservation',
        name: 'AccountUuid'
      })
      await this.$store.dispatch(LOAD_CAR_LIST, AccountUuid)
      let api = new Api()
      let { cars, carModels, total } = await api.searchCars({
        startDate,
        endDate,
        carFeatures,
        carNumber,
        carModel,
        currentCar: this.originalCar.uuid,
        AccountUuid,
        offset: (this.current - 1) * 6,
        limit: 6
      })
      this.totalAvailableCars = total
      this.availableCars = cars
      this.fields.carNumber.objects = this.carList
      this.fields.carModels.objects = carModels
    },
    closeChangeVehicleModal () {
      this.isChangingVehicle = false
      this.$store.commit(CLOSE_MODAL, 0)
    },
    vehicleUpdated (car) {
      this.selectedCar = car.uuid
      if (this.selectedCar !== this.originalCar.uuid) {
        this.$store.commit(MODIFY_FORM_FIELD, {
          permalink: 'editReservation',
          name: 'modifyNote',
          field: {
            name: 'modifyNote',
            type: 'textarea',
            label: 'Reason for changing vehicle',
            height: '100px',
            value: '',
            required: true
          }
        })
      }
      this.validate(car.uuid)
    },
    changeVehicle () {
      for (let car of this.availableCars) {
        if (car.uuid === this.selectedCar) {
          this.changedVehicle = car
        }
      }
      this.selectedCar = this.changedVehicle.uuid
      this.isChangingVehicle = false
      this.$store.commit(CLOSE_MODAL, 0)
      let oneWayTrip = this[GET_FORM_FIELD]({
        permalink: 'editReservation',
        name: 'destinationLot'
      })
      let alreadyExists = oneWayTrip.objects.filter(el => el.uuid === this.changedVehicle.Lot.uuid)
      let objects = oneWayTrip.objects
      if (!alreadyExists && this.changedVehicle.Lot.oneWayTrip) {
        objects = [...oneWayTrip.objects, ...this.changedVehicle.Lot]
      } else {
        objects = [this.changedVehicle.Lot]
      }
      this.$store.commit(MODIFY_FORM_FIELD, {
        permalink: 'editReservation',
        name: 'destinationLot',
        field: {
          name: 'destinationLot',
          type: 'one-way-trip',
          label: 'Lot',
          origin: this.changedVehicle.Lot.dropdownLabel,
          objects,
          objectName: 'dropdownLabel',
          value: this.changedVehicle.LotUuid,
          required: true
        }
      })
    },
    updateMap () {
      if (this.$refs.tabs.activeTab === 2) this.$refs.map.init(this.availableCars)
    },
    async tabChanged (tab) {
      if (tab === 2) this.$refs.map.init(this.availableCars)
    },
    updateLocation (location) {
      console.log(location)
      // this.$store.commit(MODIFY_FORM_VALUE, {
      //   permalink: 'searchReservation',
      //   name: 'address',
      //   value: {
      //     value: '',
      //     latitude: location.lat,
      //     longitude: location.lng
      //   }
      // })
    },
    async updatePage (page) {
      this.current = page
      await this.search()
    },
    validate (val = this.value) {
      this.$emit('input', val)
      if (val) {
        this.isDirty = true
        this.isValid = val.length > 0
      } else if (this.options.required) {
        this.isValid = false
        this.isDirty = true
      } else {
        this.isValid = true
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/inputs/CarChangeInput.vue